import { useState } from 'react';
import { Mail, Phone, MapPin, Send, Loader2, Check, Clock, MessageCircle } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_INFO = [
  {
    icon: Mail,
    title: 'Email Us',
    value: 'anil@poochcrew.com',
    description: "We'll respond within 24 hours",
  },
  {
    icon: Phone,
    title: 'Call Us',
    value: '1(647)293-4022',
    description: 'Mon-Fri, 9am-6pm EST',
  },
  {
    icon: MapPin,
    title: 'Visit Us',
    value: 'Ontario, Canada',
    description: 'Appointments only',
  },
];

export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setStatus('error');
      setErrorMessage('Please fill in all fields.');
      return;
    }

    setStatus('loading');
    
    try {
      await axios.post(`${API}/contact`, formData);
      setStatus('success');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      setStatus('error');
      setErrorMessage('Something went wrong. Please try again.');
    }

    // Reset after 5 seconds
    setTimeout(() => {
      setStatus('idle');
      setErrorMessage('');
    }, 5000);
  };

  return (
    <div className="min-h-screen bg-[#f5f0e6]">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-12 bg-[#f5f0e6]">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="max-w-2xl">
            <span className="font-accent text-2xl text-[#c68b4d] block mb-4">Get in Touch</span>
            <h1
              data-testid="contact-title"
              className="font-heading text-4xl md:text-6xl font-bold text-[#1e3a5f] mb-6"
            >
              We'd Love to Hear From You
            </h1>
            <p className="font-body text-lg leading-relaxed text-[#1e3a5f]/70">
              Have a question, suggestion, or just want to share cute pet photos? 
              Drop us a line and we'll get back to you as soon as possible.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="pb-24">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-6">
              {CONTACT_INFO.map((info, index) => (
                <div
                  key={info.title}
                  data-testid={`contact-info-${index}`}
                  className="bg-white p-6 rounded-2xl shadow-sm hover:shadow-md transition-all"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-[#c68b4d]/10 flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-[#c68b4d]" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-semibold text-[#1e3a5f] mb-1">
                        {info.title}
                      </h3>
                      <p className="font-body text-base text-[#1e3a5f] mb-1">
                        {info.value}
                      </p>
                      <p className="font-body text-sm text-[#1e3a5f]/60">
                        {info.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}

              {/* FAQ Link */}
              <div className="bg-[#1e3a5f] p-6 rounded-2xl text-white">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-semibold mb-2">
                      Quick Answers
                    </h3>
                    <p className="font-body text-sm text-white/70 mb-3">
                      Check our FAQ for instant answers to common questions about sizing, shipping, and returns.
                    </p>
                    <Button
                      variant="outline"
                      className="border-white text-white hover:bg-white hover:text-[#1e3a5f] rounded-full text-sm"
                    >
                      View FAQ
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white p-8 md:p-10 rounded-3xl shadow-sm">
                <h2 className="font-heading text-2xl font-semibold text-[#1e3a5f] mb-6">
                  Send us a Message
                </h2>

                {status === 'success' ? (
                  <div
                    data-testid="contact-success"
                    className="text-center py-12"
                  >
                    <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                      <Check className="w-8 h-8 text-green-600" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold text-[#1e3a5f] mb-2">
                      Message Sent!
                    </h3>
                    <p className="font-body text-[#1e3a5f]/70">
                      Thanks for reaching out. We'll get back to you within 24 hours.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="font-body text-[#1e3a5f]">
                          Your Name
                        </Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Jane Doe"
                          data-testid="contact-name-input"
                          className="h-12 rounded-xl border-[#1e3a5f]/20 focus:border-[#c68b4d] focus:ring-[#c68b4d]"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="font-body text-[#1e3a5f]">
                          Email Address
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="jane@example.com"
                          data-testid="contact-email-input"
                          className="h-12 rounded-xl border-[#1e3a5f]/20 focus:border-[#c68b4d] focus:ring-[#c68b4d]"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="subject" className="font-body text-[#1e3a5f]">
                        Subject
                      </Label>
                      <Input
                        id="subject"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="What's this about?"
                        data-testid="contact-subject-input"
                        className="h-12 rounded-xl border-[#1e3a5f]/20 focus:border-[#c68b4d] focus:ring-[#c68b4d]"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message" className="font-body text-[#1e3a5f]">
                        Message
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us more..."
                        rows={6}
                        data-testid="contact-message-input"
                        className="rounded-xl border-[#1e3a5f]/20 focus:border-[#c68b4d] focus:ring-[#c68b4d] resize-none"
                      />
                    </div>

                    {status === 'error' && (
                      <p
                        data-testid="contact-error"
                        className="font-body text-sm text-red-500"
                      >
                        {errorMessage}
                      </p>
                    )}

                    <Button
                      type="submit"
                      data-testid="contact-submit-btn"
                      disabled={status === 'loading'}
                      className="w-full h-14 bg-[#1e3a5f] hover:bg-[#1e3a5f]/90 text-white rounded-full font-semibold text-base shadow-lg hover:shadow-xl transition-all"
                    >
                      {status === 'loading' ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <Send className="w-5 h-5 ml-2" />
                        </>
                      )}
                    </Button>

                    <div className="flex items-center justify-center gap-2 text-[#1e3a5f]/60">
                      <Clock className="w-4 h-4" />
                      <span className="font-body text-sm">
                        We typically respond within 24 hours
                      </span>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
